import { Box, Flex, Grid, GridItem } from '@chakra-ui/react'
import {
  useActiveSpeakerId,
  useLocalSessionId,
  useParticipantIds,
  useParticipantProperty,
  useScreenShare
} from '@daily-co/daily-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { ParticipantTile } from '../components/participant-tile'

export const GalleryLayout = () => {
  const { screens } = useScreenShare()
  const screenShareParticipantId = screens[0]?.session_id || null
  const isScreenShareActive = !!screenShareParticipantId
  const [aspectRatio, setAspectRatio] = useState<number>(16 / 9)

  const localSessionId = useLocalSessionId()
  const activeParticipantId = useActiveSpeakerId({ ignoreLocal: true })
  const participantIds = useParticipantIds({
    filter: p => p.session_id !== localSessionId
  })

  const mainTileParticipantId = useMemo(() => {
    if (isScreenShareActive) {
      return screenShareParticipantId
    } else if (participantIds.length) {
      return activeParticipantId && participantIds.includes(activeParticipantId)
        ? activeParticipantId
        : participantIds[0]
    }

    return localSessionId
  }, [
    isScreenShareActive,
    screenShareParticipantId,
    activeParticipantId,
    participantIds
  ])

  const galleryParticipantIds = useParticipantIds({
    filter: p =>
      p.session_id !== mainTileParticipantId ||
      (isScreenShareActive && p.session_id === screenShareParticipantId),
    sort: (a, b) => {
      if (a.session_id === localSessionId) {
        return -1
      }

      if (b.session_id === localSessionId) {
        return 1
      }
      // First compare by last_active
      if (a.last_active && b.last_active) {
        const lastActiveDiff = b.last_active.getTime() - a.last_active.getTime()
        if (lastActiveDiff !== 0) return lastActiveDiff > 0 ? 1 : -1
      }
      // If last_active is equal or not available, compare by joined_at
      if (a.joined_at && b.joined_at) {
        const joinedDiff = b.joined_at.getTime() - a.joined_at.getTime()
        return joinedDiff > 0 ? 1 : -1
      }
      return 0
    }
  })

  return (
    <Flex
      w="100%"
      h="100%"
      gap={{
        base: '8px',
        md: '16px'
      }}
    >
      <Flex flex={1} justifyContent="center" alignItems="center" h="100%">
        <MainParticipantTile
          participantId={mainTileParticipantId}
          isScreenShareActive={isScreenShareActive}
        />
      </Flex>
      <Flex
        w={{
          lg: '300px',
          md: '200px',
          sm: '150px',
          base: '100px'
        }}
        flexDirection="column"
        gap="8px"
        overflowY="auto"
        height="100%"
        minHeight={0}
        sx={{
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none'
          }
        }}
      >
        {galleryParticipantIds.map(participantId => (
          <GalleryParticipantTile
            key={participantId}
            participantId={participantId}
          />
        ))}
      </Flex>
    </Flex>
  )
}

const MainParticipantTile = ({
  participantId,
  isScreenShareActive
}: {
  participantId: string
  isScreenShareActive: boolean
}) => {
  const [maxWidth, setMaxWidth] = useState<string>('100%')
  const boxRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    if (!boxRef.current) return

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }

        timeoutRef.current = setTimeout(() => {
          const height = entry.contentRect.height
          const calculatedWidth = (16 / 9) * height
          setMaxWidth(`${calculatedWidth}px`)
        }, 100)
      }
    })

    resizeObserver.observe(boxRef.current)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      resizeObserver.disconnect()
    }
  }, [])

  return (
    <Box w="100%" h="100%" maxW={maxWidth} ref={boxRef}>
      <ParticipantTile
        participantId={participantId}
        aspectRatio={16 / 9}
        isScreenShare={isScreenShareActive}
        videoFitOverride="contain"
      />
    </Box>
  )
}

const GalleryParticipantTile = ({
  participantId
}: {
  participantId: string
}) => {
  const [height, setHeight] = useState<number>(0)
  const boxRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout>()
  // const lastActive = useParticipantProperty(participantId, 'last_active')

  useEffect(() => {
    if (!boxRef.current) return

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }

        timeoutRef.current = setTimeout(() => {
          const width = entry.contentRect.width
          setHeight((9 / 16) * width)
        }, 100)
      }
    })

    resizeObserver.observe(boxRef.current)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      resizeObserver.disconnect()
    }
  }, [])

  return (
    <Box ref={boxRef} height={height + 'px'} w="100%" flexShrink={0}>
      <ParticipantTile
        key={participantId}
        borderRadius="8px"
        participantId={participantId}
        aspectRatio={16 / 9}
        videoFitOverride="contain"
      />
    </Box>
  )
}
